import React, { createContext, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { STORAGE_KEY, CLASSES } from '../utils/constants';
import { DEFAULT_SETTINGS, FONT_SIZE_DEFAULT_INDEX } from '../config/settings';
import { applyFontSize, removeFontSize, increaseFontSize, decreaseFontSize } from '../features/fontSize';
import { applyLineHeight, removeLineHeight } from '../features/lineHeight';
import { applyLetterSpacing, removeLetterSpacing } from '../features/letterSpacing';
import { applyWordSpacing, removeWordSpacing } from '../features/wordSpacing';
import { applyContrast, removeAllContrast } from '../features/contrast';
import { applyDyslexia } from '../features/dyslexia';
import { applyBoldText } from '../features/boldText';
import { applyCursor } from '../features/cursor';
import { applyCursorOverlay, removeCursorOverlay, positionCursorOverlay } from '../features/cursorOverlay';
import { applyReadingGuide, applyReadingMask, applyFocusMode } from '../features/readingGuide';
import { applyHighlightLinks, applyHighlightHeadings } from '../features/highlightLinks';
import { applyStopAnimations, applyReduceTransitions, applyDisableAutoplay } from '../features/animationControl';
import { applyHideImages } from '../features/hideImages';
import { applyKeyboardNav } from '../features/keyboardNav';
import { applyUnderlineLinks } from '../features/underlineLinks';
import { applyColorBlind, removeAllColorBlind } from '../features/colorBlind';
import { applyVisualOverlay, removeVisualOverlay } from '../features/visualOverlay';
import { toggleHtmlClass, setCssVar, removeCssVar, throttle } from '../utils/domHelpers';

export const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useLocalStorage(STORAGE_KEY, DEFAULT_SETTINGS);
  const prevSettings = useRef(settings);
  const guideRAF = useRef(null);
  const maskRAF = useRef(null);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, [setSettings]);

  const toggleSetting = useCallback((key) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (['darkContrast', 'lightContrast', 'highContrast'].includes(key) && next[key]) {
        ['darkContrast', 'lightContrast', 'highContrast'].forEach((mode) => {
          if (mode !== key) next[mode] = false;
        });
      }
      return next;
    });
  }, [setSettings]);

  const resetAll = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    removeFontSize();
    removeLineHeight();
    removeLetterSpacing();
    removeWordSpacing();
    toggleHtmlClass(CLASSES.fontSize, false);
    toggleHtmlClass(CLASSES.lineHeight, false);
    toggleHtmlClass(CLASSES.letterSpacing, false);
    toggleHtmlClass(CLASSES.wordSpacing, false);
    removeAllContrast();
    removeVisualOverlay();
    removeAllColorBlind();
    applyDyslexia(false);
    applyBoldText(false);
    applyCursor(false, false);
    applyReadingGuide(false);
    applyReadingMask(false);
    applyFocusMode(false);
    applyHighlightLinks(false);
    applyHighlightHeadings(false);
    applyStopAnimations(false);
    applyReduceTransitions(false);
    applyDisableAutoplay(false);
    applyHideImages(false);
    applyKeyboardNav(false);
    applyUnderlineLinks(false);
    removeCursorOverlay();
    if (guideRAF.current) { cancelAnimationFrame(guideRAF.current); guideRAF.current = null; }
    if (maskRAF.current) { cancelAnimationFrame(maskRAF.current); maskRAF.current = null; }
  }, [setSettings]);

  const handleFontSizeChange = useCallback((action) => {
    setSettings((prev) => {
      const nextIndex = action === 'inc'
        ? increaseFontSize(prev.fontSize)
        : action === 'dec'
          ? decreaseFontSize(prev.fontSize)
          : DEFAULT_SETTINGS.fontSize;
      return { ...prev, fontSize: nextIndex };
    });
  }, [setSettings]);

  /* ── Mouse tracking for reading guide and mask ── */
  useEffect(() => {
    if (!settings.readingGuide && !settings.readingMask) return;

    const onMouseMove = throttle((e) => {
      const y = e.clientY;

      if (settings.readingGuide) {
        setCssVar('--pb-a11y-guide-y', (y - 14) + 'px');
      }

      if (settings.readingMask) {
        const maskHeight = 140;
        const top = Math.max(0, Math.min(y - maskHeight / 2, window.innerHeight - maskHeight));
        setCssVar('--pb-a11y-mask-top', top + 'px');
        setCssVar('--pb-a11y-mask-bottom', (top + maskHeight) + 'px');
      }
    }, 16);

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMouseMove);
  }, [settings.readingGuide, settings.readingMask]);

  /* ── Mouse tracking for cursor overlay ── */
  const cursorRAF = useRef(null);

  useEffect(() => {
    if (!settings.largeCursor && !settings.extraLargeCursor) return;

    const onMouseMove = throttle((e) => {
      positionCursorOverlay(e.clientX, e.clientY);
    }, 16);

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [settings.largeCursor, settings.extraLargeCursor]);

  /* ── Apply all settings ── */
  useEffect(() => {
    const s = settings;

    /* Text features: set var + toggle class */
    applyFontSize(s.fontSize);
    toggleHtmlClass(CLASSES.fontSize, s.fontSize !== FONT_SIZE_DEFAULT_INDEX);

    applyLineHeight(s.lineHeight);
    toggleHtmlClass(CLASSES.lineHeight, s.lineHeight !== 0);

    applyLetterSpacing(s.letterSpacing);
    toggleHtmlClass(CLASSES.letterSpacing, s.letterSpacing !== 0);

    applyWordSpacing(s.wordSpacing);
    toggleHtmlClass(CLASSES.wordSpacing, s.wordSpacing !== 0);

    applyBoldText(s.boldText);
    applyDyslexia(s.dyslexiaFont);
    applyContrast('darkContrast', s.darkContrast);
    applyContrast('lightContrast', s.lightContrast);
    applyContrast('highContrast', s.highContrast);
    applyVisualOverlay('invertColors', s.invertColors);
    applyVisualOverlay('monochrome', s.monochrome);
    applyContrast('highSaturation', s.highSaturation);
    applyContrast('lowSaturation', s.lowSaturation);
    applyHighlightLinks(s.highlightLinks);
    applyHighlightHeadings(s.highlightHeadings);
    applyReadingGuide(s.readingGuide);
    applyReadingMask(s.readingMask);
    applyFocusMode(s.focusMode);
    applyCursor(s.largeCursor, false);
    const cursorEnabled = s.largeCursor || s.extraLargeCursor;
    if (cursorEnabled !== (prevSettings.current.largeCursor || prevSettings.current.extraLargeCursor)) {
      applyCursorOverlay(cursorEnabled, s.extraLargeCursor);
    } else if (cursorEnabled && s.extraLargeCursor !== prevSettings.current.extraLargeCursor) {
      applyCursorOverlay(true, s.extraLargeCursor);
    }
    applyStopAnimations(s.stopAnimations);
    applyReduceTransitions(s.reduceTransitions);
    applyHideImages(s.hideImages);
    applyKeyboardNav(s.keyboardNav);
    applyUnderlineLinks(s.underlineLinks);
    applyColorBlind('protanopia', s.protanopia);
    applyColorBlind('deuteranopia', s.deuteranopia);
    applyColorBlind('tritanopia', s.tritanopia);

    if (s.disableAutoplay !== prevSettings.current.disableAutoplay) {
      applyDisableAutoplay(s.disableAutoplay);
    }

    prevSettings.current = s;
  }, [settings]);

  const contextValue = {
    settings,
    updateSetting,
    toggleSetting,
    resetAll,
    handleFontSizeChange,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
}
